import { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { ALINEA_PROFILES, ALERT_LEVELS } from '../utils/constants.js';
import { summarizeSupervisorAlerts } from '../utils/supervisorAlerts.js';
import SupervisorFaqPanel from './SupervisorFaqPanel.jsx';

/** Columna 1 del tablero supervisor: perfil, liderazgo, preguntas y semáforo. */
export default function SupervisorProfileCard({
    teamSummary,
    alerts = [],
    leaderProfile = null,
    questionsBank = null,
}) {
    const { user } = useAuth();
    const [openLeader, setOpenLeader] = useState(true);

    const profile = ALINEA_PROFILES[user?.perfil_alinea?.toLowerCase()] || ALINEA_PROFILES.constante;
    const firstName = user?.nombre_completo?.split(' ')[0] || 'Supervisor';

    const alertStats = useMemo(() => summarizeSupervisorAlerts(alerts), [alerts]);

    const { bien = 0, regular = 0, mal = 0, tensionPct = 0, n = 0 } = teamSummary || {};

    const styleName =
        leaderProfile?.leadership_style || leaderProfile?.style || null;

    return (
        <>
            <header className="sup-arrival sup-arrival-compact">
                <p className="sup-eyebrow">Tu equipo</p>
                {user?.tenantName && (
                    <p className="sup-tenant">
                        Empresa: <strong>{user.tenantName}</strong>
                    </p>
                )}
                <h1 className="sup-greeting">Hola, {firstName}</h1>
                <p className="sup-summary-line" aria-live="polite">
                    {n === 0 ? (
                        'Aún no hay personas en tu lista.'
                    ) : (
                        <>
                            <strong>{bien}</strong> bien · <strong>{regular}</strong> regular ·{' '}
                            <strong>{mal}</strong> con día más difícil · {tensionPct}% con algo de tensión hoy.
                        </>
                    )}
                </p>
            </header>

            <section className="sup-profile-card" aria-label="Tu arquetipo ALINEA">
                <p className="sup-section-label">Tu arquetipo</p>
                <div className="sup-profile-main">
                    <span className="sup-profile-emoji" aria-hidden>
                        {profile.emoji}
                    </span>
                    <div>
                        <p className="sup-profile-name">{profile.name}</p>
                    </div>
                </div>
                {profile.daily_anchor && (
                    <p className="sup-profile-anchor">"{profile.daily_anchor}"</p>
                )}
            </section>

            <section className="sup-leader-block" aria-label="Guía de liderazgo">
                <button
                    type="button"
                    className="sup-profile-toggle"
                    onClick={() => setOpenLeader((v) => !v)}
                    aria-expanded={openLeader}
                >
                    {openLeader ? '▾' : '▸'} Guía de liderazgo
                </button>
                {openLeader && (
                    <div className="sup-leader-body">
                        {styleName ? (
                            <>
                                <p className="sup-leader-style">
                                    Estilo: <strong>{styleName}</strong>
                                </p>
                                {(leaderProfile.strengths || []).length > 0 && (
                                    <div className="sup-leader-section">
                                        <p className="sup-leader-label">💪 Fortalezas</p>
                                        <ul>
                                            {leaderProfile.strengths.map((s, i) => (
                                                <li key={i}>{s}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {(leaderProfile.blind_spots || leaderProfile.blindSpots || []).length > 0 && (
                                    <div className="sup-leader-section">
                                        <p className="sup-leader-label">🫣 Puntos ciegos</p>
                                        <ul>
                                            {(leaderProfile.blind_spots || leaderProfile.blindSpots).map(
                                                (s, i) => (
                                                    <li key={i}>{s}</li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                )}
                                {(leaderProfile.recommendations || []).length > 0 && (
                                    <div className="sup-leader-section">
                                        <p className="sup-leader-label">🎯 Recomendaciones</p>
                                        <ul>
                                            {leaderProfile.recommendations.map((s, i) => (
                                                <li key={i}>{s}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="sup-team-hint">
                                Aún no tienes perfil de liderazgo. Cuando completes el cuestionario en Coach, aparecerá
                                aquí.
                            </p>
                        )}
                    </div>
                )}
            </section>

            <SupervisorFaqPanel questionsBank={questionsBank} />

            <section className="sup-semaforo" aria-label="Alertas activas">
                <p className="sup-section-label">Alertas de tu equipo</p>
                {alertStats.total > 0 && (
                    <p className="sup-semaforo-hint">
                        {alertStats.total} alerta{alertStats.total !== 1 ? 's' : ''} activa
                        {alertStats.total !== 1 ? 's' : ''} · {alertStats.people} persona
                        {alertStats.people !== 1 ? 's' : ''} de tu equipo
                    </p>
                )}
                <div className="sup-semaforo-row">
                    {['red', 'yellow', 'green'].map((level) => (
                        <div
                            key={level}
                            className={`sup-semaforo-item sup-semaforo-${level}`}
                            title={`${ALERT_LEVELS[level].label}: ${alertStats.byLevel[level]} alerta(s)`}
                        >
                            <span className="sup-semaforo-emoji" aria-hidden>
                                {ALERT_LEVELS[level].emoji}
                            </span>
                            <span className="sup-semaforo-count">{alertStats.byLevel[level]}</span>
                            <span className="sup-semaforo-label">{ALERT_LEVELS[level].label}</span>
                        </div>
                    ))}
                </div>
            </section>
        </>
    );
}
